/*
 * Number modeling — three rows, one per place value. Each row has:
 *   • left:  selection (a clickable model of that place's block)
 *   • right: bin (collects placed blocks of that kind)
 *
 * Selection column is fixed width so the three left-edge blocks line
 * up as a single vertical stack (hundred → ten → one, top to bottom).
 * Bin widths vary by kind: tens fills the remaining width (its rods
 * are the longest line; she said this is the max-needed width), while
 * hundreds + ones bins are narrower — ones wraps after 5 cubes per
 * row, sized to exactly that width, and hundreds matches.
 */

.section {
  border-bottom: 1px solid rgba(8, 18, 12, 0.12);
  padding-bottom: var(--token-space-lg);
  margin-bottom: 0;
  display: flex;
  flex-direction: column;
  gap: var(--token-space-md);
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--token-space-md);
}

.heading {
  font-family: var(--token-font-display);
  margin: 0;
  /* Fixed design px (the card scales as a unit via useFitScale) — not vw,
   * which would reflow independently of the scaled card. */
  font-size: 33px;
  color: var(--token-text-primary);
}

.clearButton {
  min-height: 36px;
  padding: 6px 14px;
  border-radius: var(--token-radius-pill);
  border: 1px solid rgba(8, 18, 12, 0.2);
  background: transparent;
  color: rgba(28, 38, 32, 0.7);
  font-family: var(--token-font-ui);
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
}

.clearButton:hover {
  background: rgba(8, 18, 12, 0.06);
  color: var(--token-text-primary);
}

/* ---------- The tray (3 rows: hundred / ten / one) ---------- */

.tray {
  display: flex;
  flex-direction: column;
  gap: var(--token-space-lg);
}

.row {
  display: flex;
  align-items: stretch;
  gap: var(--token-space-lg);
}

/* Selection column — vertical stack of clickable block samples
 * (hundred / ten / one). Fixed width so the left edge is uniform.
 * Each button stacks the visual model on top of a kind label
 * ("Hundred" / "Ten" / "One") so kids can read what they're
 * tapping. */
.selection {
  flex: 0 0 auto;
  width: 180px;
  min-height: 180px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: rgba(255, 255, 255, 0.65);
  border: 1.5px solid rgba(8, 18, 12, 0.18);
  border-radius: 16px;
  cursor: pointer;
  padding: 16px;
  transition:
    background 120ms ease,
    border-color 120ms ease,
    transform 120ms ease,
    box-shadow 120ms ease;
}

.selection:hover {
  background: #fff;
  border-color: var(--token-accent);
  box-shadow: 0 6px 14px -6px rgba(8, 18, 12, 0.22);
}

.selection:active {
  transform: translateY(1px);
}

.selectionLabel {
  font-family: var(--token-font-ui);
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: rgba(28, 38, 32, 0.75);
}

/* ---------- The bins ---------- */

.bin {
  min-height: 160px;
  padding: 14px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.55);
  border: 1.5px dashed rgba(8, 18, 12, 0.18);
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
}

/* Hundreds bin — single horizontal row across the rest of the screen.
 * No wrap, so all placed flats line up shoulder-to-shoulder. Allows
 * horizontal overflow scroll on the unlikely chance the teacher
 * piles on enough hundreds to overrun the row. */
.bin_hundred {
  flex: 1 1 auto;
  min-width: 0;
  flex-wrap: nowrap;
  align-items: center;
  gap: 14px;
  overflow-x: auto;
}

/* Tens bin stretches to fill the remaining row width — rods line up
 * shoulder-to-shoulder along the bottom. */
.bin_ten {
  flex: 1 1 auto;
  min-width: 0;
  align-items: flex-end;
  gap: 12px 10px;
}

/* Ones bin — narrow, 5 cubes per row, wraps to a new row of 5.
 *
 * Width math (border-box is global, so width includes border + pad):
 *   5 cubes × 24px = 120 + 4 gaps × 8px = 32  →  152px content row
 *   + 1.5px border × 2 + 14px padding × 2     →  + 31
 *   = 183px minimum to fit 5 across
 *
 * Use 196 for a few pixels of slack so sub-pixel rounding can't
 * push the 5th cube to a new row. */
.bin_one {
  flex: 0 0 auto;
  width: 196px;
  align-items: flex-start;
  gap: 14px 8px;
}

/* ---------- The blocks ---------- */

.block,
.sample {
  border: 1.5px solid rgba(8, 18, 12, 0.32);
  padding: 0;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.55),
    0 1px 2px rgba(8, 18, 12, 0.15);
}

.block {
  cursor: pointer;
  transition:
    transform 120ms ease,
    box-shadow 120ms ease;
}

.block:hover {
  transform: translateY(-1px);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.6),
    0 3px 6px rgba(8, 18, 12, 0.22);
}

/* Sample inside the selection button doesn't react to pointer (the
 * button handles all hover/click state). */
.sample {
  pointer-events: none;
}

.hundred {
  width: 132px;
  aspect-ratio: 1 / 1;
  background-color: #4ea863;
  background-image:
    linear-gradient(to right, rgba(255, 255, 255, 0.55) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(255, 255, 255, 0.55) 1px, transparent 1px);
  background-size: 10% 10%;
  border-radius: 6px;
}

.ten {
  width: 24px;
  height: 132px;
  background-color: #3a78c2;
  background-image: linear-gradient(to bottom, rgba(255, 255, 255, 0.55) 1px, transparent 1px);
  background-size: 100% 10%;
  border-radius: 4px;
}

.one {
  width: 24px;
  height: 24px;
  background-color: #d65d8e;
  border-radius: 4px;
}
